import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Card, Col, Dropdown, Grid, message, Modal, Popconfirm, Row, Space} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import AgGrid from '../common/AgGrid';
import {
    createProjectWord,
    createProjectWordBatch,
    deleteProjectWordBatch,
    getProjectWordList,
    safeUpsertProjectWordBatch,
    updateProjectWord,
    upsertProjectWordBatch
} from '../../api/projectWordApi';
import ProjectWordCreateModal from './ProjectWordCreateModal';
import ExcelUploadModal from '../ExcelUploadModal';
import {AG_GRID_DEFAULT_COL_DEF, AG_GRID_LOCALE_TEXT, formatDateTime} from '../../config/agGridConfig';
import SearchComponent from '../SearchComponent';
import useApiPermission from '../../hooks/useApiPermission';
import PermissionButton from '../common/PermissionButton';
import PermissionGate from '../common/PermissionGate';

// EditCellRenderer 제거 (툴바 선택수정 사용)

function ProjectWordsTab({ pjtId, columns, pageSize = 15 }) {
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [deleting, setDeleting] = useState(false);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [uploadStats, setUploadStats] = useState(null);
  const gridRef = useRef();
  const screens = Grid.useBreakpoint();
  const isXs = !screens.md;

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getProjectWordList);
  const canCreate = canCall(createProjectWord);
  const canUploadUpsert = canCall(upsertProjectWordBatch);
  const canUploadCreate = canCall(createProjectWordBatch);
  const canUpload = canUploadUpsert || canUploadCreate;
  const canDeleteBatch = canCall(deleteProjectWordBatch);
  const canUpdate = canCall(updateProjectWord);

  const searchOptions = [
    { value: 'all', label: '전체' },
    { value: 'wordNm', label: '단어명' },
    { value: 'wordEngAbbrNm', label: '영문약어' },
    { value: 'wordEngNm', label: '영문명' },
    { value: 'wordExpln', label: '설명' },
    { value: 'domainClsfNm', label: '도메인' },
    { value: 'synm', label: '동의어' },
    { value: 'phwrd', label: '금칙어' },
  ];

  const defaultColumns = [
    { headerName: '', field: 'checkbox', headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true, width: 50, pinned: 'left', lockPosition: true, suppressMenu: true, sortable: false, filter: false, resizable: false, suppressExport: true },
    { headerName: 'UUID', field: 'pjtWordId', hide: true },
    { headerName: '단어명', field: 'wordNm', minWidth: 160, flex: 1 },
    { headerName: '영문약어', field: 'wordEngAbbrNm', minWidth: 140 },
    { headerName: '영문명', field: 'wordEngNm', minWidth: 160 },
    { headerName: '설명', field: 'wordExpln', minWidth: 240, flex: 1 },
    { headerName: '도메인', field: 'domainClsfNm', minWidth: 140 },
    { headerName: '동의어', field: 'synm', minWidth: 140 },
    { headerName: '금칙어', field: 'phwrd', minWidth: 120 },
    { headerName: '등록자', field: 'rgtr', minWidth: 100 },
    { headerName: '등록일시', field: 'regDt', minWidth: 140, valueFormatter: (p) => formatDateTime(p.value) },
    { headerName: '수정자', field: 'mdfr', minWidth: 100 },
    { headerName: '수정일시', field: 'mdfcnDt', minWidth: 140, valueFormatter: (p) => formatDateTime(p.value) },
  ];

  const fetchList = useCallback(async () => {
    if (!pjtId) return;
    try {
      if (permLoading) return;
      if (!canRead) { setRows([]); return; }
      const res = await getProjectWordList(pjtId);
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
    } catch (e) {
      setRows([]);
    }
  }, [pjtId, permLoading, canRead]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const getFilteredData = () => {
    if (!searchText.trim()) return rows;
    const keyword = searchText.toLowerCase();
    const match = (val) => val !== undefined && val !== null && String(val).toLowerCase().includes(keyword);
    return rows.filter(item => {
      if (searchColumn === 'all') { return searchOptions.filter(o => o.value !== 'all').some(o => match(item[o.value])); }
      return match(item[searchColumn]);
    });
  };

  const openEdit = useCallback((row) => {
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    setEditItem(row);
    setIsEditModalOpen(true);
  }, [permLoading, canUpdate]);

  const handleEditSelected = useCallback(() => {
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selected = gridRef.current.api.getSelectedNodes();
    if (selected.length !== 1) { message.warning('수정할 항목 1건을 선택하세요.'); return; }
    openEdit(selected[0].data);
  }, [permLoading, canUpdate, openEdit]);

  const deleteSelected = async () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (!selectedNodes.length) { message.warning('삭제할 항목을 선택하세요.'); return; }
    const idList = selectedNodes.map((n) => n.data?.pjtWordId).filter(Boolean);
    if (!idList.length) { message.warning('유효한 항목이 없습니다.'); return; }
    setDeleting(true);
    try {
      await deleteProjectWordBatch(pjtId, idList);
      await fetchList();
      gridRef.current.api.deselectAll();
      message.success(`${idList.length}개 항목을 삭제했습니다.`);
    } catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
    finally { setDeleting(false); }
  };

  const handleDownloadExcel = () => {
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const displayed = gridRef.current.columnApi
      ? gridRef.current.columnApi.getAllDisplayedColumns().map(col => {
          const def = col.getColDef();
          return { field: def.field, headerName: def.headerName || def.field, suppressExport: !!def.suppressExport };
        }).filter(c => c.field && !c.suppressExport && c.field !== 'pjtWordId')
      : (columns || defaultColumns).filter(c => c.field && !c.suppressExport && c.field !== 'pjtWordId').map(c => ({ field: c.field, headerName: c.headerName || c.field }));
    const header = ['UUID', ...displayed.map(c => c.headerName)];
    const rowsAoA = [header];
    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const dataRow = [node.data?.pjtWordId ?? ''];
      displayed.forEach(c => {
        let val = node.data?.[c.field];
        if (c.field === 'regDt' || c.field === 'mdfcnDt') val = formatDateTime(val);
        dataRow.push(val ?? '');
      });
      rowsAoA.push(dataRow);
    });
    const ws = XLSX.utils.aoa_to_sheet(rowsAoA);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Words');
    XLSX.writeFile(wb, `project-words_${pjtId}.xlsx`);
  };

  const excelColumnDefs = [
    { headerName: 'UUID', field: 'uuid', editable: true, minWidth: 220 },
    { headerName: '단어명*', field: 'wordNm', editable: true, minWidth: 160 },
    { headerName: '영문약어*', field: 'wordEngAbbrNm', editable: true, minWidth: 140 },
    { headerName: '영문명*', field: 'wordEngNm', editable: true, minWidth: 160 },
    { headerName: '설명*', field: 'wordExpln', editable: true, minWidth: 220, flex: 1 },
    { headerName: '도메인', field: 'domainClsfNm', editable: true, minWidth: 140 },
    { headerName: '동의어', field: 'synm', editable: true, minWidth: 140 },
    { headerName: '금칙어', field: 'phwrd', editable: true, minWidth: 120 },
  ];

  const excelFieldMapping = ['uuid','wordNm','wordEngAbbrNm','wordEngNm','wordExpln','domainClsfNm','synm','phwrd'];
  const excelGuideBase = '열 순서: UUID | 단어명* | 영문약어* | 영문명* | 설명* | 도메인 | 동의어 | 금칙어';
  const excelGuide = excelGuideBase + (canUploadUpsert ? '\nUUID 동일하면 UPDATE, 없거나 비어있으면 INSERT 처리됩니다.' : '\n업서트 권한 없음: UUID 값은 무시/신규 생성 전용 (기존 UUID 업데이트 불가)');

  const confirmDeleteFromMenu = () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (!selectedNodes.length) { message.warning('삭제할 항목을 선택하세요.'); return; }
    Modal.confirm({ title: '선택 항목 삭제', content: '선택된 항목을 삭제하시겠습니까?', okText: '삭제', okButtonProps: { danger: true }, cancelText: '취소', onOk: deleteSelected });
  };

  const actionMenuItems = [
    ...(canCreate ? [{ key: 'create', icon: <PlusOutlined />, label: '신규생성' }] : []),
    ...(canUpdate ? [{ key: 'edit', icon: <EditOutlined />, label: '선택 항목 수정' }] : []),
    ...(canDeleteBatch ? [{ type: 'divider' }, { key: 'delete', icon: <DeleteOutlined />, label: '선택 항목 삭제', danger: true }] : []),
    ...(canUpload ? [{ type: 'divider' }, { key: 'upload', icon: <UploadOutlined />, label: canUploadUpsert ? '엑셀 업로드(업서트)' : '엑셀 업로드' }] : []),
    { key: 'download', icon: <DownloadOutlined />, label: '엑셀 다운로드' },
    ...(canRead ? [{ type: 'divider' }, { key: 'refresh', icon: <ReloadOutlined />, label: '새로고침' }] : []),
  ];

  const onActionMenuClick = ({ key }) => {
    switch (key) {
      case 'create': (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true); break;
      case 'edit': handleEditSelected(); break;
      case 'delete': confirmDeleteFromMenu(); break;
      case 'upload': (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true); break;
      case 'download': handleDownloadExcel(); break;
      case 'refresh': (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList(); break;
      default: break;
    }
  };

  const getContextMenuItems = useCallback((params) => {
    const items = [];
    if (params?.node?.data) {
      items.push({ name: '수정', action: () => openEdit(params.node.data) });
      items.push('separator');
    }
    items.push('copy');
    items.push('separator');
    items.push('export');
    return items;
  }, [openEdit]);

  return (
    <PermissionGate require={getProjectWordList}>
      {({allowed}) => (
        <div style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24}>
                {isXs ? (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SearchComponent searchColumn={searchColumn} setSearchColumn={setSearchColumn} searchText={searchText} setSearchText={setSearchText} searchOptions={searchOptions} defaultSearchColumn={'wordNm'} />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} placement="bottomRight" trigger={["click"]}>
                        <Button aria-label="���업 메뉴" icon={<EllipsisOutlined />} />
                      </Dropdown>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                      <SearchComponent searchColumn={searchColumn} setSearchColumn={setSearchColumn} searchText={searchText} setSearchText={setSearchText} searchOptions={searchOptions} defaultSearchColumn={'wordNm'} />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                      <Space wrap>
                        <PermissionButton type="primary" icon={<PlusOutlined />} onClick={() => (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true)} disabled={permLoading || !canCreate} require={createProjectWord}>신규생성</PermissionButton>
                        <PermissionButton icon={<EditOutlined />} type="default" onClick={handleEditSelected} disabled={permLoading || !canUpdate} require={updateProjectWord}>선택수정</PermissionButton>
                        <Popconfirm title="선택 항목 삭제" description="선택된 항목을 삭제하시겠습니까?" onConfirm={deleteSelected} okText="삭제" cancelText="취소" disabled={permLoading || !canDeleteBatch}>
                          <PermissionButton danger icon={<DeleteOutlined />} disabled={permLoading || !canDeleteBatch || deleting} loading={deleting} require={deleteProjectWordBatch}>선택 항목 삭제</PermissionButton>
                        </Popconfirm>
                        <PermissionButton type="primary" icon={<UploadOutlined />} onClick={() => (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canUpload} require={canUploadUpsert ? upsertProjectWordBatch : createProjectWordBatch} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>{canUploadUpsert ? '엑셀 업로드(업서트)' : '엑셀 업로드'}</PermissionButton>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadExcel} style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>엑셀 다운로드</Button>
                        <PermissionButton icon={<ReloadOutlined />} onClick={() => (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList()} disabled={permLoading || !canRead} require={getProjectWordList}>새로고침</PermissionButton>
                      </Space>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card>

          <div style={{ width: '100%' }}>
            <AgGrid ref={gridRef} rowData={getFilteredData()} columnDefs={columns || defaultColumns} defaultColDef={AG_GRID_DEFAULT_COL_DEF} paginationPageSize={pageSize} rowSelection="multiple" localeText={AG_GRID_LOCALE_TEXT}
              onRowDoubleClicked={(e) => e?.data && openEdit(e.data)} getContextMenuItems={getContextMenuItems} />
          </div>

          <ExcelUploadModal isOpen={isExcelModalOpen} onClose={() => { setIsExcelModalOpen(false); }} onSuccess={async () => { await fetchList(); }} agGridColumnDefs={excelColumnDefs} agGridDefaultColDef={AG_GRID_DEFAULT_COL_DEF} fieldMapping={excelFieldMapping} uploadApi={async (rows) => {
            const normalized = rows.map(r => {
              const base = {
                uuid: r.uuid || undefined,
                pjtWordId: r.uuid || r.pjtWordId,
                wordNm: r.wordNm?.trim?.() || '',
                wordEngAbbrNm: r.wordEngAbbrNm?.trim?.() || '',
                wordEngNm: r.wordEngNm?.trim?.() || '',
                wordExpln: r.wordExpln || '',
                domainClsfNm: r.domainClsfNm || '',
                synm: r.synm || '',
                phwrd: r.phwrd || '',
              };
              // 업서트 권한 없으면 uuid 제거하여 항상 신규 생성 유도
              if (!canUploadUpsert) {
                base.uuid = undefined;
                base.pjtWordId = undefined;
              }
              return base;
            });
            const result = await safeUpsertProjectWordBatch(pjtId, normalized, { forceClient: !canUploadUpsert });
            setUploadStats(result);
          }} excelGuide={excelGuide} rowKey="rowKey" />

          {uploadStats && (
            <div style={{ marginTop: 12 }}>
              <Card size="small" style={{ background:'#fafafa' }}>
                업로드 결과: serverUpsert={String(uploadStats.serverUsed)} | updated={uploadStats.updated} | created={uploadStats.created} | errors={uploadStats.errors.length}
              </Card>
            </div>
          )}

          <ProjectWordCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={fetchList} pjtId={pjtId} />
          <ProjectWordCreateModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSuccess={async () => { await fetchList(); setEditItem(null); }} pjtId={pjtId} mode="edit" initialValues={editItem} />
        </div>
      )}
    </PermissionGate>
  );
}

export default ProjectWordsTab;
